import React from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Printer, FileText, Mail, X, CalendarDays } from 'lucide-react';

interface ActionBarProps {
  onGeneratePDF: () => void;
  onGenerateFacturX?: () => void; // Optionnel - uniquement pour factures
  onSendEmail: () => void;
  onPrint: () => void;
  onClose: () => void;
  onPaymentSchedules?: () => void; // Optionnel pour le bouton échéancier
  isLoading: boolean;
  documentType?: 'invoice' | 'quote' | 'credit-note';
  accentColor?: string; // Design System Volvo v2 - Teal par défaut
  invoiceStatus?: string; // Statut de la facture pour désactiver certaines actions
}

/**
 * Barre d'actions pour les templates de documents
 * Affiche les boutons : PDF, Factur-X, Email, Print, Close
 */
export const ActionBar: React.FC<ActionBarProps> = ({
  onGeneratePDF,
  onGenerateFacturX,
  onSendEmail,
  onPrint,
  onClose,
  onPaymentSchedules,
  isLoading,
  documentType = 'invoice',
  accentColor = '#0D9488', // Design System Volvo v2 - Teal-600
  invoiceStatus
}) => {
  const { t } = useTranslation();
  // Statuts pour lesquels l'échéancier est désactivé
  const isScheduleDisabled = invoiceStatus === 'PAID' || invoiceStatus === 'CANCELED';
  const scheduleDisabledReason = invoiceStatus === 'PAID'
    ? t('templates.common.invoiceAlreadyPaid')
    : invoiceStatus === 'CANCELED'
      ? t('templates.common.invoiceCanceled')
      : undefined;
  // Style bouton blanc transparent glassmorphism
  const buttonStyle = "flex items-center gap-3 w-full px-4 py-3 bg-white/30 hover:bg-white/50 backdrop-blur-sm text-gray-700 rounded-xl transition-all duration-300 disabled:opacity-50 font-medium text-sm border border-white/20";

  return (
    <div className="fixed bottom-6 right-6 print:hidden z-50">
      {/* Container glassmorphism avec boutons verticaux */}
      <div className="bg-white/20 backdrop-blur-xl rounded-2xl shadow-xl border border-white/30 p-3 flex flex-col gap-2">
        {/* Retour */}
        <button
          onClick={onClose}
          disabled={isLoading}
          className={buttonStyle}
        >
          <X className="w-5 h-5" />
          <span>{t('templates.common.back')}</span>
        </button>

        {/* Imprimer */}
        <button
          onClick={onPrint}
          disabled={isLoading}
          className={buttonStyle}
        >
          <Printer className="w-5 h-5" />
          <span>{t('templates.common.print')}</span>
        </button>

        {/* PDF */}
        <button
          onClick={onGeneratePDF}
          disabled={isLoading}
          className={buttonStyle}
          title={t('templates.common.downloadPDF')}
        >
          <Download className="w-5 h-5" />
          <span>{isLoading ? t('templates.common.generating') : "PDF"}</span>
        </button>

        {/* Factur-X (uniquement pour factures) */}
        {documentType === 'invoice' && (
          <button
            onClick={onGenerateFacturX}
            disabled={isLoading}
            className={buttonStyle}
            title={t('templates.common.generateFacturX')}
          >
            <FileText className="w-5 h-5" />
            <span>{isLoading ? t('templates.common.generating') : "Factur-X"}</span>
          </button>
        )}

        {/* Email */}
        <button
          onClick={onSendEmail}
          disabled={isLoading}
          className={buttonStyle}
          title={documentType === 'invoice' ? t('templates.common.sendInvoiceByEmail') : t('templates.common.sendQuoteByEmail')}
        >
          <Mail className="w-5 h-5" />
          <span>Email</span>
        </button>

        {/* Échéancier (uniquement pour factures, désactivé si payée/annulée) */}
        {documentType === 'invoice' && onPaymentSchedules && (
          <button
            onClick={onPaymentSchedules}
            disabled={isLoading || isScheduleDisabled}
            className={buttonStyle}
            title={scheduleDisabledReason || t('templates.common.managePaymentSchedule')}
          >
            <CalendarDays className="w-5 h-5" />
            <span>{t('templates.common.schedule')}</span>
          </button>
        )}
      </div>
    </div>
  );
};
